import { useEffect } from "react";
import {
  ArrowDownRight,
  ArrowRight,
  CalendarClock,
  Image,
  MonitorPlay,
  RefreshCw,
  Volume2,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { PlatformBranding } from "./adminOperations.js";

interface LandingPageProps {
  branding: PlatformBranding;
}

const managedServices = [
  {
    className: "landing-service landing-service--content",
    icon: Image,
    title: "Content prepared for the room",
    description:
      "Announcements, offers, menus, videos, and branded visuals are shaped for the screens your customers actually see.",
  },
  {
    className: "landing-service landing-service--schedule",
    icon: CalendarClock,
    title: "Right message, right time",
    description:
      "Playlists and schedules keep morning, evening, seasonal, and campaign content organized without daily intervention.",
  },
  {
    className: "landing-service landing-service--updates",
    icon: RefreshCw,
    title: "Remote updates",
    description:
      "Changes reach connected displays automatically, whether you operate one location or a growing group of sites.",
  },
  {
    className: "landing-service landing-service--sound",
    icon: Volume2,
    title: "Motion and sound",
    description:
      "Video, YouTube, transitions, and managed background audio turn passive televisions into useful communication surfaces.",
  },
];

export function LandingPage({ branding }: LandingPageProps) {
  useEffect(() => {
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    if (description)
      description.content =
        "Managed digital signage for businesses that need polished, current content across every television screen.";
    return () => {
      if (description && previousDescription !== undefined)
        description.content = previousDescription;
    };
  }, []);

  return (
    <main className="landing-page" id="top">
      <a className="landing-skip-link" href="#landing-content">
        Skip to main content
      </a>

      <header className="landing-header" aria-label="Primary navigation">
        <Link className="landing-brand" to="/" aria-label={`${branding.name} home`}>
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt="" />
          ) : (
            <span className="landing-brand__mark" aria-hidden="true">
              <MonitorPlay size={22} />
            </span>
          )}
          <span>{branding.name}</span>
        </Link>
        <nav className="landing-nav" aria-label="Landing page">
          <a href="#managed-service">What we manage</a>
          <a href="#how-it-works">How it works</a>
        </nav>
        <div className="landing-header__actions">
          {branding.clientDashboardEnabled ? (
            <Link className="landing-text-link" to="/login">
              Client sign in
            </Link>
          ) : null}
          <Link className="landing-button landing-button--compact" to="/player">
            Activate a TV <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      <div id="landing-content">
        <section className="landing-hero" aria-labelledby="landing-title">
          <div className="landing-hero__copy">
            <span className="landing-eyebrow">Managed digital signage</span>
            <h1 id="landing-title">Your screens, handled.</h1>
            <p>
              We manage the content, timing, and updates. Your displays stay current without another
              system to run.
            </p>
            <div className="landing-hero__actions">
              <a className="landing-button" href="#how-it-works">
                See how it works <ArrowDownRight size={17} />
              </a>
              {branding.clientDashboardEnabled ? (
                <Link className="landing-quiet-link" to="/login">
                  Open client workspace <ArrowRight size={16} />
                </Link>
              ) : null}
            </div>
          </div>
          <figure className="landing-hero__visual">
            <img
              src="/managed-signage-hero.webp"
              alt="A modern reception area with two professionally installed digital displays"
              width="1920"
              height="1201"
              fetchPriority="high"
            />
            <figcaption>
              <span>One managed service</span>
              <strong>Every screen stays on message.</strong>
            </figcaption>
          </figure>
        </section>

        <section
          className="landing-managed landing-section"
          id="managed-service"
          aria-labelledby="managed-title"
        >
          <div className="landing-section__intro">
            <h2 id="managed-title">A polished screen should not create more work.</h2>
            <p>
              Q-Assist combines content management, scheduling, playback, and support into one
              managed service for customer-facing spaces.
            </p>
          </div>
          <div className="landing-service-grid">
            {managedServices.map(({ className, icon: Icon, title, description }) => (
              <article className={className} key={title}>
                <Icon size={22} aria-hidden="true" />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="landing-process landing-section"
          id="how-it-works"
          aria-labelledby="process-title"
        >
          <div className="landing-process__heading">
            <span>From request to screen</span>
            <h2 id="process-title">Simple for your team. Controlled behind the scenes.</h2>
          </div>
          <ol className="landing-process__steps">
            <li>
              <span>01</span>
              <div>
                <h3>Tell us what needs to show</h3>
                <p>Share the message, campaign, menu, or media you want customers to see.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <h3>We prepare the display</h3>
                <p>Content is organized, branded, scheduled, and checked for the target screen.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <h3>Your televisions stay current</h3>
                <p>Connected players receive updates and continue showing approved content.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="landing-spaces landing-section" aria-labelledby="spaces-title">
          <div className="landing-spaces__copy">
            <h2 id="spaces-title">Made for places where attention matters.</h2>
            <p>
              Clear information, consistent branding, and timely updates across the spaces your
              customers visit.
            </p>
          </div>
          <div className="landing-spaces__list" aria-label="Supported business spaces">
            <span>Restaurants</span>
            <span>Clinics</span>
            <span>Schools</span>
            <span>Retail</span>
            <span>Offices</span>
            <span>Waiting areas</span>
          </div>
        </section>

        <section className="landing-closing" aria-labelledby="closing-title">
          <div>
            <h2 id="closing-title">Turn the television into a useful part of the space.</h2>
            <p>Connect a display securely with its current six-digit activation code.</p>
          </div>
          <Link className="landing-button landing-button--light" to="/player">
            Open TV activation <ArrowRight size={17} />
          </Link>
        </section>
      </div>

      <footer className="landing-footer">
        <span>{branding.name}</span>
        <p>Managed digital signage for customer-facing spaces.</p>
        <Link to="/player">TV player</Link>
      </footer>
    </main>
  );
}
